import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAuth, authService } from "auth";
import { AuthFormField } from "components/auth-form/AuthForm";
import card_services from "services/card_services";
import {
  cardNumberSelector,
  networkTypeSelector,
} from "store/userSliceHelpers";
import { useRefreshCard } from "pages/user-page/useRefreshCard";
import { apiErrorMessage } from "utils/errors/parseApiError";
import {
  SIGNED_OUT_MESSAGE,
  attemptsLeftMessage,
  classifyReauthFailure,
  clearReauthAttempts,
  spendReauthAttempt,
} from "utils/reauth";

export type SuspendCardStep = 1 | 2;

export interface UseSuspendCardReturn {
  step: SuspendCardStep;
  title: string;
  subtitle: string;
  fields: AuthFormField[];
  submitRef: React.RefObject<HTMLButtonElement>;
  submitLabel: string;
  isSubmitDisabled: boolean;
  /** Masked PAN for the confirmation copy, e.g. "•••• 3336". Empty when no card is linked. */
  maskedCard: string;
  showLoading: boolean;
  loadingMsg: string;
  showError: boolean;
  errorMsg: string;
  showSuccess: boolean;
  successMsg: string;
  /** True once the last allowed wrong password was spent: closing now ends the session. */
  endsSession: boolean;
  handleSubmit: () => Promise<void>;
  /** Step 2 -> step 1. The password is already proved; this only re-shows the field. */
  handleBack: () => void;
  handleConfirmSuspend: () => Promise<void>;
  handleCloseError: () => void;
  handleCloseSuccess: () => void;
  reset: () => void;
}

/**
 * Suspend the cardholder's own card: prove the password, confirm the consequences, then suspend.
 */
export function useSuspendCard(): UseSuspendCardReturn {
  const { signOut } = useAuth();
  const cardNumber = useSelector(cardNumberSelector);
  const networkType = useSelector(networkTypeSelector);
  const { handleRefresh } = useRefreshCard();

  const [step, setStep] = useState<SuspendCardStep>(1);
  const [currentPassword, setCurrentPassword] = useState("");

  const [showLoading, setShowLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const signOutOnCloseRef = useRef(false);
  const [endsSession, setEndsSession] = useState(false);

  const runIdRef = useRef(0);
  const passwordRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  const isStale = (run: number) => runIdRef.current !== run;

  const consumePendingSignOut = () => {
    if (!signOutOnCloseRef.current) return;
    signOutOnCloseRef.current = false;
    setEndsSession(false);
    void signOut();
  };

  // A route change out of /user unmounts this modal with no close handler running; without this
  // the spent lockout is forgotten and the session survives.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => consumePendingSignOut(), []);

  const clearError = () => {
    setShowError(false);
    setErrorMsg("");
  };

  const openError = (msg: string) => {
    setLoadingMsg("");
    setShowLoading(false);
    setErrorMsg(msg);
    setShowError(true);
  };

  const handleCloseError = () => clearError();

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setSuccessMsg("");
  };

  const reset = () => {
    runIdRef.current += 1;
    setStep(1);
    setCurrentPassword("");
    clearError();
    setShowLoading(false);
    setLoadingMsg("");
    setShowSuccess(false);
    setSuccessMsg("");
    // Last, because it navigates: every close path runs reset, so a pending sign-out cannot be
    // escaped by dismissing the modal instead of the error.
    consumePendingSignOut();
  };

  const maskedCard = cardNumber ? `•••• ${cardNumber.slice(-4)}` : "";

  const submitPassword = async () => {
    if (!currentPassword)
      return openError("Please enter your current password.");

    const run = runIdRef.current;
    try {
      setLoadingMsg("Checking your current password…");
      setShowLoading(true);
      await authService.reauthenticate(currentPassword);
      if (isStale(run)) return;
      clearReauthAttempts();
      signOutOnCloseRef.current = false;
      setEndsSession(false);
      setShowLoading(false);
      setLoadingMsg("");
      setCurrentPassword("");
      setStep(2);
    } catch (err: any) {
      const { message, isWrongPassword } = classifyReauthFailure(
        err,
        "We couldn't verify your current password. Please try again.",
      );
      if (!isWrongPassword) {
        if (isStale(run)) return;
        return openError(message);
      }

      // Spent before the staleness check: closing the modal on a wrong guess must not dodge it.
      const left = spendReauthAttempt();
      if (isStale(run)) return;
      setCurrentPassword("");

      if (left <= 0) {
        // No focus() here: the field stays mounted behind the error overlay, which traps neither
        // focus nor keystrokes — parking the caret there would invite typing past the lockout.
        signOutOnCloseRef.current = true;
        setEndsSession(true);
        return openError(SIGNED_OUT_MESSAGE);
      }
      passwordRef.current?.focus();
      openError(attemptsLeftMessage(message, left));
    }
  };

  const handleSubmit = async () => {
    // The lockout is terminal, and it must be checked before clearError wipes its message.
    if (signOutOnCloseRef.current) return;
    clearError();
    if (step === 1) await submitPassword();
  };

  const handleBack = () => {
    clearError();
    setStep(1);
  };

  const handleConfirmSuspend = async () => {
    if (signOutOnCloseRef.current) return;
    if (!cardNumber) return openError("No card is linked to this account.");
    clearError();

    const run = runIdRef.current;
    try {
      setLoadingMsg("Suspending your card…");
      setShowLoading(true);
      const result: any = await card_services.suspendCard(
        cardNumber,
        networkType,
      );
      if (isStale(run)) return;
      await handleRefresh();
      if (isStale(run)) return;
      setShowLoading(false);
      setLoadingMsg("");
      setSuccessMsg(
        result?.alreadyApplied
          ? "This card was already suspended. It can't be used for payments or refunds. Contact support to reactivate it."
          : "Your card has been suspended. It can no longer be used for payments or refunds. Contact support to reactivate it.",
      );
      setShowSuccess(true);
    } catch (err: any) {
      if (isStale(run)) return;
      openError(
        apiErrorMessage(err, "We couldn't suspend your card. Please try again."),
      );
    }
  };

  const submitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") submitRef.current?.click();
  };

  const passwordFields: AuthFormField[] = [
    {
      name: "reauthPassword",
      label: "Current Password",
      type: "password",
      value: currentPassword,
      onChange: setCurrentPassword,
      onKeyDown: submitOnEnter,
      placeholder: "Enter your current password",
      autoComplete: "current-password",
      inputRef: passwordRef,
    },
  ];

  const titles: Record<SuspendCardStep, string> = {
    1: "Confirm your password",
    2: "Suspend this card",
  };

  const subtitles: Record<SuspendCardStep, string> = {
    1: "For your security, confirm your current password before suspending your card.",
    2: maskedCard
      ? `Read this carefully before suspending card ${maskedCard}.`
      : "Read this carefully before suspending your card.",
  };

  const fieldsByStep: Record<SuspendCardStep, AuthFormField[]> = {
    1: passwordFields,
    2: [],
  };

  const submitLabels: Record<SuspendCardStep, string> = {
    1: "Continue",
    2: "Suspend card",
  };

  const submitDisabled: Record<SuspendCardStep, boolean> = {
    1: !currentPassword,
    2: !cardNumber,
  };

  return {
    step,
    title: titles[step],
    subtitle: subtitles[step],
    fields: fieldsByStep[step],
    submitRef,
    submitLabel: submitLabels[step],
    isSubmitDisabled: endsSession || submitDisabled[step],
    maskedCard,
    showLoading,
    loadingMsg,
    showError,
    errorMsg,
    showSuccess,
    successMsg,
    endsSession,
    handleSubmit,
    handleBack,
    handleConfirmSuspend,
    handleCloseError,
    handleCloseSuccess,
    reset,
  };
}
